import type { AtTagData, HashTagData } from "../util/tag.js";
import type { Entity, JSXElement, JSXNode, MarkupOptions } from "shelving";
import { MARKUP_RULES, NamedRegExpMarkupRule, getFirstLine, getJSXNodeElements, getJSXNodeText, limitString, renderMarkup as shelvingRenderMarkup } from "shelving";
import { AT_TAG_REGEXP, ENTITY_TAG_REGEXP, HASH_TAG_REGEXP } from "../util/tag.js";
import { getEntityPath } from "./entity.js";

const $$typeof = Symbol.for("react.element");

/** JSXElement including hash, at, or entity tag data. */
export type JSXTagElement = JSXElement & { hash?: HashTagData; at?: AtTagData; entity?: Entity };

/** Markup rule to convert topic tags to links, e.g. `#running` */
export const HASH_TAG_RULE = new NamedRegExpMarkupRule<HashTagData>(
	HASH_TAG_REGEXP,
	(hash): JSXTagElement => {
		const { slug } = hash;
		return {
			type: "a",
			key: null,
			ref: null,
			$$typeof,
			props: {
				className: "link",
				href: `/tag/${slug}`,
				children: `@${slug}`,
			},
			hash, // Save the tag data so we can use it later in `getTagElements()`
		};
	},
	["inline", "list"],
);

/** Markup rule to convert username tags to links, e.g. `@dhoulb` or `@dhoulb/30-day-running-challenge */
export const AT_TAG_RULE = new NamedRegExpMarkupRule<AtTagData>(
	AT_TAG_REGEXP,
	(at): JSXTagElement => {
		const { username, slug } = at;
		return {
			type: "a",
			key: null,
			ref: null,
			$$typeof,
			props: {
				className: "link",
				href: slug ? `/${username}/${slug}` : `/${username}`,
				children: slug ? `@${username}/${slug}` : `/${username}`,
			},
			at, // Save the tag data so we can use it later in `getTagElements()`
		};
	},
	["inline", "list"],
);

/** Markup rule to convert entity tags to links, e.g. `challenge:a7sdj2d` */
export const ENTITY_TAG_RULE = new NamedRegExpMarkupRule<{ entity: Entity }>(
	ENTITY_TAG_REGEXP,
	({ entity }): JSXTagElement => {
		return {
			type: "a",
			key: null,
			ref: null,
			$$typeof,
			props: {
				className: "link",
				href: getEntityPath(entity),
				children: entity,
			},
			entity, // Save the tag data so we can use it later in `getTagElements()`
		};
	},
	["inline", "list"],
);

/** Render markup including `@at` and `#topic` and `entity:` tags. */
export const renderMarkup = (content: string): JSXNode => shelvingRenderMarkup(content, OPTIONS);
const OPTIONS: Partial<MarkupOptions> = {
	rules: [
		AT_TAG_RULE, //
		HASH_TAG_RULE,
		ENTITY_TAG_RULE,
		...MARKUP_RULES,
	],
	rel: "nofollow ugc",
};

/** Get the text of the first line of markup content. */
export function getMarkupFirstLine(content: string) {
	// Strips markup characters, e.g. `Some **bold** text` will return `Some bold text`
	return getJSXNodeText(renderMarkup(getFirstLine(content)));
}

/** Get a `max` character excerpt of the text of the first line of markup content. */
export function getMarkupExcerpt(content: string, max = 100) {
	// Limiting rounds to the nearest full word because it looks awful if a word is broken halfway.
	return limitString(getMarkupFirstLine(content), max);
}

/** Is an unknown JSXElement a JSXTagElement? */
const isTagElement = (el: JSXElement | JSXTagElement): el is JSXTagElement => "hash" in el || "at" in el || "entity" in el;

/** Extract the tags from text by rendering the markup then crawling for tag elements. */
export function* getTagElements(content: string): Iterable<JSXTagElement> {
	for (const el of getJSXNodeElements(renderMarkup(content))) if (isTagElement(el)) yield el;
}
